/**
 * Incident Report Tab Component
 * Manages incident documentation with database persistence
 */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { AlertTriangle, Printer, Trash2, Save, History, Edit, FileText } from "lucide-react";
import { format } from "date-fns";
import { exportIncidentReportPDF } from "@/utils/pdfExport";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOCATIONS = [
  { value: "classroom", label: "Classroom" },
  { value: "playground", label: "Playground" },
  { value: "bathroom", label: "Bathroom" },
  { value: "hallway", label: "Hallway" },
  { value: "cafeteria", label: "Cafeteria/Eating Area" },
  { value: "nap_area", label: "Nap Area" },
  { value: "gym", label: "Gym/Activity Room" },
  { value: "entrance", label: "Entrance/Lobby" },
  { value: "parking", label: "Parking Lot" },
  { value: "other", label: "Other" }
];

const FIRST_AID_OPTIONS = [
  { value: "none", label: "None Required" },
  { value: "bandaid", label: "Band-Aid/Bandage" },
  { value: "ice_pack", label: "Ice Pack" },
  { value: "cleaned_wound", label: "Cleaned Wound" },
  { value: "antiseptic", label: "Antiseptic Applied" },
  { value: "comfort", label: "Comfort/Reassurance Only" },
  { value: "rest", label: "Rest Period" },
  { value: "multiple", label: "Multiple Treatments" },
  { value: "ems", label: "EMS Called" },
  { value: "other", label: "Other" }
];

export const IncidentReportTab = ({ children, centerName = '' }) => {
  const [incidentDate, setIncidentDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [incidentChildId, setIncidentChildId] = useState("");
  const [incidentWitnesses, setIncidentWitnesses] = useState("");
  const [incidentDescription, setIncidentDescription] = useState("");
  const [incidentResolution, setIncidentResolution] = useState("");
  const [incidentStaffName, setIncidentStaffName] = useState("");
  const [incidentTimeHour, setIncidentTimeHour] = useState("12");
  const [incidentTimeMinute, setIncidentTimeMinute] = useState("00");
  const [incidentTimePeriod, setIncidentTimePeriod] = useState("PM");
  const [incidentLocation, setIncidentLocation] = useState("");
  const [incidentLocationOther, setIncidentLocationOther] = useState("");
  const [incidentFirstAid, setIncidentFirstAid] = useState("");
  const [incidentFirstAidOther, setIncidentFirstAidOther] = useState("");
  const [incidentReports, setIncidentReports] = useState([]);
  const [savingIncident, setSavingIncident] = useState(false);
  const [editingIncidentId, setEditingIncidentId] = useState(null);
  const [showIncidentHistory, setShowIncidentHistory] = useState(false);

  useEffect(() => {
    fetchIncidentReports();
  }, []);

  const fetchIncidentReports = async () => {
    try {
      const res = await axios.get(`${API}/incident-reports`);
      setIncidentReports(res.data?.reports || []);
    } catch (error) {
      console.error("Error fetching incident reports:", error);
    }
  };

  const getChildName = (childId) => {
    const child = children.find(c => c.id === childId);
    return child ? child.name : "Not Selected";
  };

  const getFormattedTime = () => `${incidentTimeHour}:${incidentTimeMinute} ${incidentTimePeriod}`;

  const handleSaveIncidentReport = async () => {
    if (!incidentChildId || !incidentDescription || !incidentResolution) {
      toast.error("Please fill in child, description, and resolution fields");
      return;
    }
    
    setSavingIncident(true);
    try {
      const reportData = {
        date: incidentDate,
        time: getFormattedTime(),
        childId: incidentChildId,
        childName: getChildName(incidentChildId),
        location: incidentLocation,
        locationOther: incidentLocation === 'other' ? incidentLocationOther : '',
        witnesses: incidentWitnesses,
        description: incidentDescription,
        firstAid: incidentFirstAid,
        firstAidOther: incidentFirstAid === 'other' ? incidentFirstAidOther : '',
        resolution: incidentResolution,
        staffName: incidentStaffName
      };
      
      if (editingIncidentId) {
        await axios.put(`${API}/incident-reports/${editingIncidentId}`, reportData);
        toast.success("Incident report updated");
        setEditingIncidentId(null);
      } else {
        await axios.post(`${API}/incident-reports`, reportData);
        toast.success("Incident report saved to database");
      }
      
      await fetchIncidentReports();
      handleClearIncidentForm();
    } catch (error) {
      console.error("Error saving incident report:", error);
      toast.error("Failed to save incident report");
    } finally {
      setSavingIncident(false);
    }
  };

  const handleEditIncidentReport = (report) => {
    setEditingIncidentId(report.id);
    setIncidentDate(report.date || format(new Date(), 'yyyy-MM-dd'));
    if (report.time) {
      const timeParts = report.time.match(/(\d+):(\d+)\s*(AM|PM)/i);
      if (timeParts) {
        setIncidentTimeHour(timeParts[1]);
        setIncidentTimeMinute(timeParts[2]);
        setIncidentTimePeriod(timeParts[3].toUpperCase());
      }
    }
    setIncidentChildId(report.childId || "");
    setIncidentLocation(report.location || "");
    setIncidentLocationOther(report.locationOther || "");
    setIncidentWitnesses(report.witnesses || "");
    setIncidentDescription(report.description || "");
    setIncidentFirstAid(report.firstAid || "");
    setIncidentFirstAidOther(report.firstAidOther || "");
    setIncidentResolution(report.resolution || "");
    setIncidentStaffName(report.staffName || "");
    setShowIncidentHistory(false);
    toast.info("Loaded report for editing");
  };

  const handleDeleteIncidentReport = async (reportId) => {
    if (!window.confirm("Are you sure you want to delete this incident report?")) return;
    
    try {
      await axios.delete(`${API}/incident-reports/${reportId}`);
      toast.success("Incident report deleted");
      await fetchIncidentReports();
    } catch (error) {
      console.error("Error deleting incident report:", error);
      toast.error("Failed to delete incident report");
    }
  };

  const handleClearIncidentForm = () => {
    setIncidentDate(format(new Date(), 'yyyy-MM-dd'));
    setIncidentTimeHour("12");
    setIncidentTimeMinute("00");
    setIncidentTimePeriod("PM");
    setIncidentChildId("");
    setIncidentWitnesses("");
    setIncidentDescription("");
    setIncidentResolution("");
    setIncidentStaffName("");
    setIncidentLocation("");
    setIncidentLocationOther("");
    setIncidentFirstAid("");
    setIncidentFirstAidOther("");
    setEditingIncidentId(null);
  };

  const handlePrint = () => window.print();

  const handleExportPDF = async () => {
    try {
      const reportData = {
        date: incidentDate,
        time: getFormattedTime(),
        childName: getChildName(incidentChildId),
        staffName: incidentStaffName,
        location: incidentLocation === 'other' && incidentLocationOther 
          ? `Other: ${incidentLocationOther}` 
          : incidentLocation,
        firstAid: incidentFirstAid === 'other' && incidentFirstAidOther 
          ? `Other: ${incidentFirstAidOther}` 
          : incidentFirstAid,
        witnesses: incidentWitnesses,
        description: incidentDescription,
        resolution: incidentResolution
      };
      await exportIncidentReportPDF(reportData, centerName);
      toast.success("Incident report PDF downloaded");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to export PDF");
    }
  };

  return (
    <Card className="bg-white/90 backdrop-blur-sm border-2 border-rose-200">
      <CardHeader className="print:hidden">
        <CardTitle className="text-2xl text-rose-800 flex items-center gap-3">
          <AlertTriangle className="w-7 h-7" />
          Incident Report
          {editingIncidentId && (
            <span className="text-sm font-normal bg-yellow-100 text-yellow-800 px-2 py-1 rounded ml-2">
              Editing Report
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Image */}
        <div className="mb-6 print:hidden">
          <img 
            src="https://images.unsplash.com/photo-1754951661104-66d2151d9f40?w=800"
            alt="Child in doctor costume"
            className="w-full h-48 object-cover rounded-lg shadow-md"
          />
        </div>

        {/* Form Row 1: Date, Time, Child, Staff */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 print:hidden">
          <div>
            <Label className="text-rose-800 font-semibold">Date *</Label>
            <Input
              type="date"
              value={incidentDate}
              onChange={(e) => setIncidentDate(e.target.value)}
              className="border-rose-200 mt-1"
            />
          </div>

          <div>
            <Label className="text-rose-800 font-semibold">Time of Incident *</Label>
            <div className="flex gap-1 mt-1">
              <Select value={incidentTimeHour} onValueChange={setIncidentTimeHour}>
                <SelectTrigger className="border-rose-200 w-20">
                  <SelectValue placeholder="Hr" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({length: 12}, (_, i) => i + 1).map((h) => (
                    <SelectItem key={h} value={String(h)}>{h}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="flex items-center text-rose-800 font-bold">:</span>
              <Select value={incidentTimeMinute} onValueChange={setIncidentTimeMinute}>
                <SelectTrigger className="border-rose-200 w-20">
                  <SelectValue placeholder="Min" />
                </SelectTrigger>
                <SelectContent>
                  {['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={incidentTimePeriod} onValueChange={setIncidentTimePeriod}>
                <SelectTrigger className="border-rose-200 w-20">
                  <SelectValue placeholder="AM/PM" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AM">AM</SelectItem>
                  <SelectItem value="PM">PM</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-rose-800 font-semibold">Child Involved *</Label>
            <Select value={incidentChildId} onValueChange={setIncidentChildId}>
              <SelectTrigger className="border-rose-200 mt-1">
                <SelectValue placeholder="Select child" />
              </SelectTrigger>
              <SelectContent>
                {children.sort((a, b) => a.name.localeCompare(b.name)).map((child) => (
                  <SelectItem key={child.id} value={child.id}>{child.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-rose-800 font-semibold">Staff Reporting *</Label>
            <Input
              value={incidentStaffName}
              onChange={(e) => setIncidentStaffName(e.target.value)}
              placeholder="Enter your name"
              className="border-rose-200 mt-1"
            />
          </div>
        </div>

        {/* Form Row 2: Location, First Aid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 print:hidden">
          <div>
            <Label className="text-rose-800 font-semibold">Incident Location *</Label>
            <div className="flex gap-2 mt-1">
              <Select value={incidentLocation} onValueChange={(val) => { setIncidentLocation(val); if (val !== 'other') setIncidentLocationOther(''); }}>
                <SelectTrigger className="border-rose-200 flex-1">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {LOCATIONS.map(loc => (
                    <SelectItem key={loc.value} value={loc.value}>{loc.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {incidentLocation && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => { setIncidentLocation(''); setIncidentLocationOther(''); }}
                  className="text-gray-400 hover:text-gray-600 px-2"
                  title="Click to reset"
                >
                  ✕
                </Button>
              )}
            </div>
            {incidentLocation === 'other' && (
              <Input
                value={incidentLocationOther}
                onChange={(e) => setIncidentLocationOther(e.target.value)}
                placeholder="Describe the location..."
                className="border-rose-200 mt-2"
              />
            )}
          </div>

          <div>
            <Label className="text-rose-800 font-semibold">First Aid Applied</Label>
            <div className="flex gap-2 mt-1">
              <Select value={incidentFirstAid} onValueChange={(val) => { setIncidentFirstAid(val); if (val !== 'other') setIncidentFirstAidOther(''); }}>
                <SelectTrigger className="border-rose-200 flex-1">
                  <SelectValue placeholder="Select first aid type" />
                </SelectTrigger>
                <SelectContent>
                  {FIRST_AID_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {incidentFirstAid && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => { setIncidentFirstAid(''); setIncidentFirstAidOther(''); }}
                  className="text-gray-400 hover:text-gray-600 px-2"
                  title="Click to reset"
                >
                  ✕
                </Button>
              )}
            </div>
            {incidentFirstAid === 'other' && (
              <Input
                value={incidentFirstAidOther}
                onChange={(e) => setIncidentFirstAidOther(e.target.value)}
                placeholder="Describe the first aid applied..."
                className="border-rose-200 mt-2"
              />
            )}
          </div>
        </div>

        {/* Witnesses */}
        <div className="mb-6 print:hidden">
          <Label className="text-rose-800 font-semibold">Witnesses (if any)</Label>
          <Input
            value={incidentWitnesses}
            onChange={(e) => setIncidentWitnesses(e.target.value)}
            placeholder="Names of any witnesses"
            className="border-rose-200 mt-1"
          />
        </div>

        {/* Report Preview */}
        <div className="border-2 border-rose-100 rounded-lg p-6 bg-white print:border-0 print:p-0">
          <div className="text-center mb-6 border-b-2 border-rose-200 pb-4 print:border-black">
            <h2 className="text-2xl font-bold text-rose-800 print:text-black">Incident Report</h2>
            <p className="text-sm text-gray-600 mt-1 print:text-black">Confidential Document</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-left">
              <div>
                <span className="font-semibold">Date:</span>
                <p className="text-rose-700 print:text-black">
                  {incidentDate ? format(new Date(incidentDate + 'T12:00:00'), 'MMMM d, yyyy') : "Not Set"}
                </p>
              </div>
              <div>
                <span className="font-semibold">Time:</span>
                <p className="text-rose-700 print:text-black">{getFormattedTime()}</p>
              </div>
              <div>
                <span className="font-semibold">Child:</span>
                <p className="text-rose-700 print:text-black">{getChildName(incidentChildId)}</p>
              </div>
              <div>
                <span className="font-semibold">Staff:</span>
                <p className="text-rose-700 print:text-black">{incidentStaffName || "Not Entered"}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4 text-left">
              <div>
                <span className="font-semibold">Location:</span>
                <p className="text-rose-700 print:text-black capitalize">
                  {incidentLocation === 'other' && incidentLocationOther 
                    ? `Other: ${incidentLocationOther}` 
                    : (incidentLocation ? incidentLocation.replace('_', ' ') : "Not Selected")}
                </p>
              </div>
              <div>
                <span className="font-semibold">First Aid Applied:</span>
                <p className="text-rose-700 print:text-black capitalize">
                  {incidentFirstAid === 'other' && incidentFirstAidOther 
                    ? `Other: ${incidentFirstAidOther}` 
                    : (incidentFirstAid ? incidentFirstAid.replace('_', ' ') : "Not Selected")}
                </p>
              </div>
            </div>
            {incidentWitnesses && (
              <div className="mt-4 text-left">
                <span className="font-semibold">Witnesses:</span>
                <p className="text-rose-700 print:text-black">{incidentWitnesses}</p>
              </div>
            )}
          </div>

          {/* Description */}
          <div className="mb-6">
            <Label className="text-rose-800 font-semibold print:text-black text-lg">Description of Incident *</Label>
            <p className="text-sm text-gray-500 mb-2 print:hidden">Describe what happened in detail</p>
            <Textarea
              value={incidentDescription}
              onChange={(e) => setIncidentDescription(e.target.value)}
              placeholder="Provide a detailed description of the incident..."
              className="border-rose-200 mt-2 min-h-[150px] print:border-black"
            />
          </div>

          {/* Resolution */}
          <div className="mb-6">
            <Label className="text-rose-800 font-semibold print:text-black text-lg">Resolution / Action Taken *</Label>
            <p className="text-sm text-gray-500 mb-2 print:hidden">Describe how the incident was handled</p>
            <Textarea
              value={incidentResolution}
              onChange={(e) => setIncidentResolution(e.target.value)}
              placeholder="Describe the resolution and any actions taken..."
              className="border-rose-200 mt-2 min-h-[100px] print:border-black"
            />
          </div>

          {/* Signature Lines for Print */}
          <div className="mt-8 grid grid-cols-2 gap-8 print:block hidden">
            <div className="border-t border-black pt-2">
              <p className="font-semibold">Staff Signature</p>
              <p className="text-sm text-gray-600">Date: _____________</p>
            </div>
            <div className="border-t border-black pt-2">
              <p className="font-semibold">Director/Supervisor Signature</p>
              <p className="text-sm text-gray-600">Date: _____________</p>
            </div>
          </div>

          <div className="mt-6 p-3 bg-rose-50 border border-rose-200 rounded-lg print:bg-white print:border-black">
            <p className="text-rose-800 font-semibold text-center print:text-black">
              REMINDER: Print a copy for Center Records
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-between flex-wrap gap-2 print:hidden">
          <div className="flex gap-2">
            <Button onClick={handleClearIncidentForm} variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50">
              <Trash2 className="w-4 h-4 mr-2" />
              Clear Form
            </Button>
            <Button onClick={() => setShowIncidentHistory(!showIncidentHistory)} variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-50">
              <History className="w-4 h-4 mr-2" />
              History ({incidentReports.length})
            </Button>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleSaveIncidentReport}
              disabled={!incidentChildId || !incidentDescription || !incidentResolution || savingIncident}
              className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
            >
              {savingIncident ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </div>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {editingIncidentId ? "Update Report" : "Save Report"}
                </>
              )}
            </Button>
            <Button onClick={handleExportPDF} variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50">
              <FileText className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            <Button onClick={handlePrint} className="bg-rose-600 hover:bg-rose-700 text-white">
              <Printer className="w-4 h-4 mr-2" />
              Print Report
            </Button>
          </div>
        </div>

        {/* History Panel */}
        {showIncidentHistory && (
          <div className="mt-6 border-2 border-purple-200 rounded-lg p-4 bg-purple-50">
            <h3 className="text-lg font-semibold text-purple-800 mb-4">Saved Incident Reports</h3>
            {incidentReports.length === 0 ? (
              <p className="text-gray-500 italic">No saved incident reports yet.</p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {incidentReports.map((report) => (
                  <div key={report.id} className="bg-white p-3 rounded-lg border border-purple-200 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-purple-800">{report.childName}</p>
                      <p className="text-sm text-gray-600">
                        {report.date ? format(new Date(report.date + 'T12:00:00'), 'MMM d, yyyy') : 'No date'} at {report.time || 'No time'}
                      </p>
                      <p className="text-sm text-gray-500 truncate max-w-md">{report.description?.substring(0, 80)}...</p>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => handleEditIncidentReport(report)} size="sm" variant="outline" className="border-purple-300 text-purple-700">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button onClick={() => handleDeleteIncidentReport(report.id)} size="sm" variant="outline" className="border-red-300 text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default IncidentReportTab;
